import { useRef, useEffect, useContext } from "react"
import { google, outlook, ics } from "calendar-link"
import { LocaleContext } from "contexts"
import H2 from "components/atoms/H2"
import Button from "components/buttons/Button"
import { RichText } from "prismic-reactjs"
import WavingAvatareAnimation from "components/Avatar/bodymovin/waving_00.json"
import useTranslations from "hooks/useTranslations"

const Content = ({ children }) => {
  return (
    <div className="prismic-rich-text space-y-4 text-base">
      <RichText render={children} />
    </div>
  )
}

export default function Success({ location }) {
  const { state } = location
  const event = {
    title: state?.title,
    start: state?.start,
    end: state?.end,
    // duration: [1, "hour"] as CalendarEvent["duration"],
    location: state?.url,
  }

  const {
    webinarFormSuccessTitle,
    webinarFormSuccessBody,
    trainingSuccess1,
    trainingSuccess2,
    trainingSuccess3,
    trainingSuccess4,
    addToCalendarTitle,
    addToCalendarGoogle,
    addToCalendarOutlook,
    addToCalendarICS,
  } = useTranslations()

  const animationContainer = useRef()

  const { locale } = useContext(LocaleContext)

  useEffect(() => {
    let anim
    import("lottie-web").then((lottie) => {
      anim = lottie.default.loadAnimation({
        container: animationContainer.current,
        animationData: WavingAvatareAnimation,
      })
    })
    return () => anim?.destroy()
  }, [])

  return (
    <>
      <div className="mx-auto mt-36 flex max-w-7xl bg-white px-4 pb-6 text-zinc-800 sm:px-6 lg:px-8 lg:pb-12">
        <div className="flex flex-col items-start space-y-8 lg:flex-row lg:space-x-8 lg:space-y-0">
          <div
            className="-mb-4 -ml-10 -mt-32 h-36 w-36 lg:-ml-24 lg:-mt-24 lg:mb-0 lg:h-52 lg:w-52"
            ref={animationContainer}
          />
          <div className="">
            <H2 textAlign="left">{webinarFormSuccessTitle}</H2>
            <div>
              <p>
                {state?.segment === "schools"
                  ? trainingSuccess1
                  : webinarFormSuccessBody}
              </p>
              {state?.url ? (
                <p>
                  {trainingSuccess2}
                  <a
                    href={state?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {state?.url}
                  </a>
                  {trainingSuccess3}
                </p>
              ) : null}
              <p>{trainingSuccess4}</p>
            </div>
          </div>
        </div>
      </div>
      {state ? (
        <>
          <div className="mx-auto bg-zinc-100 pb-6 md:py-12 lg:pb-12">
            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
              <div className="space-y-6 text-zinc-800">
                <H2 textAlign="left">{addToCalendarTitle}</H2>
                <div className="flex flex-col justify-start space-y-6 md:flex-row md:space-x-8 md:space-y-0">
                  <Button
                    label={addToCalendarGoogle}
                    link={google(event)}
                    outbound
                  />
                  <Button
                    label={addToCalendarOutlook}
                    link={outlook(event)}
                    outbound
                  />
                  <Button label={addToCalendarICS} link={ics(event)} outbound />
                </div>
              </div>
            </div>
          </div>
          <div className="mx-auto max-w-7xl bg-white ">
            <div className="mx-auto px-4 py-16 text-zinc-800 sm:px-6 lg:px-8">
              <div className="flex max-w-2xl space-x-4 lg:space-x-8">
                {state?.start ? (
                  <div className="w-min">
                    <div className="h-8 bg-red-400 px-2 pt-1 text-center font-bold text-slate-50">
                      {state.start.toLocaleDateString(locale, {
                        month: "long",
                      })}
                    </div>
                    <div className="bg-slate-50 px-1 py-2 text-center text-5xl font-bold leading-none text-slate-900">
                      {state.start.getDate()}
                    </div>
                    <div className="bg-slate-700 px-1 py-2 text-center text-lg font-bold leading-none text-slate-50">
                      {`0${state.start.getHours()}`.slice(-2)}:
                      {`0${state.start.getMinutes()}`.slice(-2)}
                    </div>
                  </div>
                ) : null}
                <div className="space-y-4">
                  <h2 className="text-3xl lg:text-5xl">{state?.title}</h2>
                  {state?.description?.map((section) => (
                    <Content>{section}</Content>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </>
  )
}
